import { Box, debounce, TextField } from '@material-ui/core';
import {
  Autocomplete,
  AutocompleteInputChangeReason,
  AutocompleteProps,
} from '@material-ui/lab';
import React, {
  ChangeEvent,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import {
  Maybe,
  SearchServiceAddress,
  ServiceAddress,
} from '../../__generated__/pge-types';
import { useServiceAddressQuery } from './useServiceAddressQuery';
import { TextFieldProps } from '../text-field/TextField';
import { useTranslation } from '../../hooks/useTranslation';
import useUtil from '../need-more-time-to-pay/useUtil';
import Checkbox from '@material-ui/core/Checkbox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';

export enum SelectedAddressDisplayType {
  'address',
  'addressCityZip',
}

export interface Props {
  // autocompleteProps: See MUI Autocomplete API documentaion
  autocompleteProps?: Omit<
    AutocompleteProps<SearchServiceAddress>,
    | 'options'
    | 'filterOptions'
    | 'noOptionsText'
    | 'getOptionLabel'
    | 'loading'
    | 'renderInput'
    | 'renderOption'
  >;
  // inputFieldProps: See MUI TextField API documentaion
  inputFieldProps?: TextFieldProps;
  searchResultsPlaceHolderText?: string;
  noSearchResultsText?: string;
  selectedAddressDisplay?: SelectedAddressDisplayType;
  onAddressChange: (value: any) => void;
  multiple?: boolean;
  onInputFieldClear?: () => Promise<void>;
  setAddressSuggestions?: React.Dispatch<
    React.SetStateAction<Maybe<SearchServiceAddress>[]>
  >;
  setServiceAddressQueryLoader?: React.Dispatch<React.SetStateAction<boolean>>;
  selectedAddressList?: ServiceAddress[];
  selectedData?: any;
  setLoading?: (isLoading: boolean) => void;
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const ServiceAddressLookup = ({
  autocompleteProps,
  inputFieldProps,
  searchResultsPlaceHolderText,
  noSearchResultsText,
  selectedAddressDisplay = SelectedAddressDisplayType.addressCityZip,
  multiple,
  onAddressChange,
  onInputFieldClear,
  setAddressSuggestions,
  setServiceAddressQueryLoader,
  selectedAddressList,
  selectedData,
  setLoading,
}: Props) => {
  const {
    loading,
    data,
    filterData,
    error,
    setSearchString,
    searchString,
  } = useServiceAddressQuery();

  const { setErrorNotification } = useUtil();
  const { t } = useTranslation();
  const textFieldRef = useRef<HTMLInputElement>(null);

  const [noOptionsText, setNoOptionsText] = useState<string | undefined>(
    searchResultsPlaceHolderText,
  );

  const setNewSearchString = (newSearchString: string) => {
    newSearchString &&
      newSearchString.trim().length >= 3 &&
      setSearchString(newSearchString);
  };

  const debounceSetSearchString = useMemo(
    //ref: https://css-tricks.com/debouncing-throttling-explained-examples/
    () => debounce(setNewSearchString, 250),
    [],
  );

  const getOptionLabel = (serviceAddress: SearchServiceAddress): string => {
    if (selectedAddressDisplay === SelectedAddressDisplayType.address) {
      return serviceAddress?.addressLine1;
    }
    if (selectedAddressDisplay === SelectedAddressDisplayType.addressCityZip) {
      return `${serviceAddress?.addressLine1}, ${serviceAddress?.city}, ${serviceAddress?.postal}`;
    }
    return ''; //unreachable due to the selectedAddressDisplay default value
  };

  useEffect(() => {
    if (setLoading) {
      setLoading(loading);
    }
  }, [loading]);

  useEffect(() => {
    if (setServiceAddressQueryLoader) {
      setServiceAddressQueryLoader(loading);
    }
  }, [loading]);

  useEffect(() => {
    if (error) {
      setErrorNotification(true);
    } else {
      setErrorNotification(false);
    }
  }, [error]);

  useEffect(() => {
    if (
      data &&
      data?.searchServiceAddress &&
      data?.searchServiceAddress?.addresses &&
      data?.searchServiceAddress?.addresses.length < 1
    ) {
      setNoOptionsText(noSearchResultsText);
    } else {
      if (setAddressSuggestions) {
        setAddressSuggestions(data?.searchServiceAddress.addresses!);
      }
    }

    if (!searchString) {
      setNoOptionsText(
        searchResultsPlaceHolderText ||
          t('START_TYPING_TO_SEARCH_FOR_ADDRESS_SELECT'),
      );
    }
  }, [data, searchString]);

  return (
    <>
      <Autocomplete
        multiple={multiple}
        {...autocompleteProps}
        options={
          (filterData?.searchServiceAddress
            ?.addresses as SearchServiceAddress[]) || []
        }
        filterOptions={options => options}
        getOptionLabel={option => {
          return getOptionLabel(option);
        }}
        onChange={async (_e: ChangeEvent<{}>, newValue: any) => {
          if (multiple) {
            await onAddressChange(newValue);
          } else {
            if (newValue?.addressLine1) {
              await onAddressChange({
                ...newValue,
              });
            } else {
              await onAddressChange({
                addressLine1: '',
                city: '',
                postal: '',
                premiseIds: undefined,
              });
            }
          }
        }}
        onInputChange={async (
          _e: ChangeEvent<{}>,
          _value: string,
          reason: AutocompleteInputChangeReason,
        ) => {
          if (reason === 'clear') {
            if (onInputFieldClear) {
              await onInputFieldClear();
            }
            await onAddressChange({
              addressLine1: '',
              city: '',
              postal: '',
              premiseIds: undefined,
            });
          }
        }}
        noOptionsText={noOptionsText}
        loading={loading}
        renderInput={params => (
          <TextField
            {...params}
            {...inputFieldProps}
            inputProps={{
              ...params.inputProps,
              maxLength: 60,
              // adding this to disable the browser auto complete so that it doesn't clash with autocomplete component
              //ref: https://stackoverflow.com/questions/48304062/material-ui-textfield-disable-browser-autocomplete
              autoComplete: 'new-password',
              ...inputFieldProps?.inputProps,
            }}
            InputProps={{
              ...params.InputProps,
              ...inputFieldProps?.InputProps,
            }}
            inputRef={textFieldRef}
            onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
              debounceSetSearchString(e.target.value);
              if (inputFieldProps?.onChange) {
                const pos = textFieldRef?.current?.selectionStart || 0;
                await inputFieldProps.onChange(e);
                textFieldRef?.current?.setSelectionRange(pos, pos);
              }
            }}
          />
        )}
        disableCloseOnSelect={!!multiple}
        renderOption={(option, state) => {
          setTimeout(() => {
            const optionEl = document.querySelector(
              `[data-name="${
                (selectedData || [])[(selectedData || [])?.length - 1]
              }"]`,
            );
            optionEl?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
              inline: 'nearest',
            });
          }, 0.5);
          return (
            <>
              {multiple ? (
                <li
                  style={{ display: 'flex' }}
                  data-name={`${option?.addressLine1?.trim()}, ${option?.city?.trim()}, ${option?.postal?.trim()}`}
                >
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={
                      selectedAddressList?.length &&
                      selectedAddressList.filter(
                        add =>
                          option.addressLine1.trim().toLowerCase() ===
                            add?.addressLine1?.trim().toLowerCase() &&
                          option.city.trim().toLowerCase() ===
                            add?.city?.trim().toLowerCase() &&
                          option.postal.trim().toLowerCase() ===
                            add?.postal?.trim().toLowerCase(),
                      ).length
                        ? true
                        : state?.selected
                    }
                  />
                  <Box alignItems={'center'}>
                    {`${option?.addressLine1}, ${option?.city}, ${option?.postal}`}
                  </Box>
                </li>
              ) : (
                <Box alignItems={'center'}>
                  {`${option?.addressLine1}, ${option?.city}, ${option?.postal}`}
                </Box>
              )}
            </>
          );
        }}
        onOpen={() => {
          setTimeout(() => {
            const optionEl = document.querySelector(
              `[data-name="${
                (selectedData || [])[(selectedData || [])?.length - 1]
              }"]`,
            );
            optionEl?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
              inline: 'nearest',
            });
          }, 0.5);
        }}
      />
    </>
  );
};
export default ServiceAddressLookup;
